import React, { useState, useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Breadcrumbs from "./Breadcrumbs";
import CatalogueSidebar from "./CatalogueSidebar";
import {
  ArrowUpDown,
  ShoppingCart,
  Heart,
  GitCompare,
  Package,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { getDocumentUrl, productService, vehicleCatalogService } from "../../services/apiService";
import { useVehicle } from "../../contexts/VehicleContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useCart } from "../../contexts/CartContext";
import { useAuth } from "../../auth/AuthContext";
import TierComparisonModal from "./TierComparisonModal";
import CatalogDarkFilterSidebar from "./CatalogDarkFilterSidebar";
import { getPartDetailPath } from "../../utils/partDetailRoutes";
import CatalogueDarkPartCard from "../CatalogueDarkPartCard";
import CatalogueComparePartCard from "../CatalogueComparePartCard";
import { buildCatalogCompareDummyProducts } from "./catalogCompareDummyData";

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};
const PART_PLACEHOLDER_DATA_URI =
  "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='180' viewBox='0 0 240 180'%3E%3Crect width='240' height='180' fill='%23f3f4f6'/%3E%3Crect x='16' y='16' width='208' height='148' rx='12' fill='%23e5e7eb'/%3E%3Cg fill='%239ca3af'%3E%3Ccircle cx='88' cy='80' r='18'/%3E%3Cpath d='M40 136l38-38 24 24 34-34 64 48z'/%3E%3C/g%3E%3Ctext x='120' y='164' text-anchor='middle' font-size='14' fill='%236b7280' font-family='Arial,sans-serif'%3ENo image%3C/text%3E%3C/svg%3E";

/** Avoid "BMW" + "BMW i5" when model already includes make — fixes compatibility matching + display. */
function stripLeadMakeFromModel(model, make) {
  const m = String(model || "").trim();
  const mk = String(make || "").trim();
  if (!m || !mk) return m;
  const lowerM = m.toLowerCase();
  const prefix = `${mk.toLowerCase()} `;
  if (lowerM.startsWith(prefix)) {
    return m.slice(mk.length).trim() || m;
  }
  return m;
}

function isCatalogTestOrDummyPart(p) {
  if (!p) return true;
  if (p.isDummy || String(p.sourceType || "").toLowerCase() === "dummy") return true;
  if (String(p.id || p._id || "").startsWith("demo-")) return true;
  if (String(p.id || p._id || "").startsWith("dummy-")) return true;
  const name = String(p.name || "").toLowerCase();
  const sku = String(p.sku || p.partNumber || "").toLowerCase();
  if (name.includes("testing only") || name.includes("(testing)")) return true;
  if (/\bdummy\b/.test(name)) return true;
  if (sku.startsWith("test-") || sku === "test-sp-001") return true;
  return false;
}

/** Drop incomplete catalog stubs (e.g. "brake" / Unknown / ₹0 / OEM 123). */
function isIncompleteCatalogListing(p) {
  if (!p) return true;
  const linked =
    p.productId && typeof p.productId === "object" ? p.productId : null;
  const name = String(p.name || linked?.name || p.partName || "").trim();
  const brand = String(p.brand || linked?.brand || p.manufacturer || "").trim();
  const partNumber = String(
    p.partNumber || linked?.partNumber || p.sku || ""
  ).trim();
  const price = Number(p.price ?? linked?.price ?? p.sellingPrice ?? 0);
  const nameLc = name.toLowerCase();
  const brandLc = brand.toLowerCase();
  const pnLc = partNumber.toLowerCase();

  if (!name || name.length < 2) return true;
  if (/\b(quisquam|molest|deserunt|dicta|lorem|ipsum)\b/i.test(name)) return true;
  if (/^(123|n\/?a|na|test|xxx+|000+)$/i.test(partNumber)) return true;
  if (/x{3,}/i.test(partNumber) && (!brand || brandLc === "unknown") && price <= 1) {
    return true;
  }

  const genericOneWord =
    /^(brake|brakes|engine|filter|filters|oil|clutch|battery|tyre|tire|wheel|gearbox|radiator|seats?|headlight|fuel)$/i.test(
      nameLc
    );
  const weakBrand = !brand || brandLc === "unknown" || brandLc === "testbrand";
  if (genericOneWord && weakBrand) return true;
  if (genericOneWord && price <= 0 && (!partNumber || pnLc === "n/a")) return true;
  if (weakBrand && price <= 0 && (!partNumber || pnLc === "n/a" || pnLc === "123")) {
    return true;
  }
  return false;
}

function shouldHideCatalogPart(p) {
  return isCatalogTestOrDummyPart(p) || isIncompleteCatalogListing(p);
}

/** Vehicle-catalog nodes are only useful when linked to a real marketplace product. */
function isLinkedSellableCatalogPart(p) {
  if (shouldHideCatalogPart(p)) return false;
  const linkedId =
    (p?.productId && typeof p.productId === "object"
      ? p.productId._id || p.productId.id
      : p?.productId) || p?.linkedProductId;
  const price = Number(
    p?.price ??
      (p?.productId && typeof p.productId === "object" ? p.productId.price : 0) ??
      p?.sellingPrice ??
      0
  );
  const brand = String(
    p?.brand ||
      (p?.productId && typeof p.productId === "object" ? p.productId.brand : "") ||
      ""
  ).trim();
  // Prefer linked products; allow unlinked only when they look like real listings
  if (linkedId) return true;
  if (brand && brand.toLowerCase() !== "unknown" && price > 0) return true;
  return false;
}

/** Split picker values like "C300 Petrol" → { variant: "C300", fuelType: "Petrol" }. */
function splitVariantAndFuel(rawVariant, existingFuel) {
  const raw = String(rawVariant || "").trim();
  const fuel = String(existingFuel || "").trim();
  if (!raw) return { variant: "", fuelType: fuel };
  const fuelMatch = raw.match(/\b(petrol|diesel|cng|lpg|electric|hybrid|ev)\b/i);
  if (!fuelMatch) return { variant: raw, fuelType: fuel };
  const detectedFuel = /ev/i.test(fuelMatch[1]) ? "Electric" : fuelMatch[1];
  const variant = raw.replace(fuelMatch[0], "").replace(/\s+/g, " ").trim();
  return {
    variant: variant || raw,
    fuelType: fuel || detectedFuel,
  };
}

/** Used-part grade for condition filters (API or text fallback). */
const getProductUsedGrade = (p) => {
  const letter = String(p.usedGrade || "").toUpperCase();
  if (letter === "A") return "excellent";
  if (letter === "B") return "good";
  if (letter === "C") return "fair";
  const raw = `${p.usedConditionRaw || ""}`.toLowerCase();
  if (raw.includes("excellent") || raw.includes("excel")) return "excellent";
  if (raw.includes("good")) return "good";
  if (raw.includes("fair")) return "fair";
  return "unknown";
};

/** Minimal product tile: image + name only (light catalogue pages). */
const ClassificationTableProductCard = ({ product, getProductDetailRoute, categoryData }) => {
  const to = getProductDetailRoute(product);
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm h-full">
      <Link
        to={to}
        state={{ product, category: categoryData }}
        aria-label={`View ${product.name}`}
        className="group flex flex-col items-center text-center gap-3 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
      >
        <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-lg bg-gray-50 border border-gray-100 overflow-hidden flex items-center justify-center">
          <img
            loading="lazy"
            src={product.image}
            alt={product.name || "Spare part"}
            className="w-full h-full object-contain p-2"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = PART_PLACEHOLDER_DATA_URI;
            }}
          />
        </div>
        <span className="text-sm sm:text-base font-semibold text-gray-900 line-clamp-2 leading-snug">
          {product.name || "Spare Part"}
        </span>
      </Link>
    </div>
  );
};

/**
 * Generic Category Product List Component
 * Reusable for all categories: Belt, Brake, Engine Oil, Filters, Engine, etc.
 * Accepts category data and displays products
 */
const CategoryProductList = ({
  categoryName = "Products",
  categorySlug = "",
  products = EMPTY_ARRAY,
  defaultFilters = EMPTY_OBJECT,
  description = null,
  footerContent = null,
  isEmbedded = false,
  /** When false, URL ?origin=… does not pre-check sidebar filters (keeps 3-column compare visible on /catalog). */
  applyUrlOriginToSidebar = true,
  /** Sparepé-style dark catalogue: hero search, chips, New/Used tabs, chrome aligned with marketing mockups. */
  embeddedCatalogChrome = false,
  /** When true (with embeddedCatalogChrome), search + chips row is rendered in parent; use paired parent* props. */
  catalogToolbarInParent = false,
  parentListSearch = "",
  onParentListSearchChange = null,
  parentSortBy = "relevance",
  onParentSortChange = null,
  parentAppliedLocationFilters = null,
  onParentAppliedLocationFiltersChange = null,
  parentMobileSidebarOpen = false,
  onParentMobileSidebarOpenChange = null
}) => {
  const { category } = useParams();
  const location = useLocation();
  const { selectedVehicle } = useVehicle();
  const { isDark } = useTheme();
  /** Enhanced layout (full filters + compact grid) on /catalog or whenever dark theme is on. */
  const useEnhancedCatalog = Boolean(embeddedCatalogChrome) || isDark;
  /** Color tokens follow the real theme toggle — never force dark chrome in light mode. */
  const isDarkCatalog = isDark;
  const [internalSortBy, setInternalSortBy] = useState("relevance");
  const sortBy = catalogToolbarInParent ? parentSortBy : internalSortBy;
  const setSortBy = catalogToolbarInParent ? onParentSortChange : setInternalSortBy;
  const [viewMode, setViewMode] = useState("list");
  const [selectedOrigin, setSelectedOrigin] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [fulfilledBySparelo, setFulfilledBySparelo] = useState(false);
  const [freeDelivery, setFreeDelivery] = useState(false);
  const [darkInStockOnly, setDarkInStockOnly] = useState(false);
  const [darkRating4Plus, setDarkRating4Plus] = useState(false);
  const [darkRating3Plus, setDarkRating3Plus] = useState(false);
  const [darkCondNew, setDarkCondNew] = useState(false);
  const [darkCondUsedExcellent, setDarkCondUsedExcellent] = useState(false);
  const [darkCondUsedGood, setDarkCondUsedGood] = useState(false);
  const [darkCondUsedFair, setDarkCondUsedFair] = useState(false);
  const [darkPriceMax, setDarkPriceMax] = useState(95000);
  const [darkPriceMinStr, setDarkPriceMinStr] = useState("");
  const [darkWarranty6m, setDarkWarranty6m] = useState(false);
  const [darkWarranty1y, setDarkWarranty1y] = useState(false);
  const [darkWarranty2y, setDarkWarranty2y] = useState(false);
  const [brandsExpanded, setBrandsExpanded] = useState(false);
  const [internalMobileOpen, setInternalMobileOpen] = useState(false);
  const hasControlledMobileSidebar =
    catalogToolbarInParent && typeof onParentMobileSidebarOpenChange === "function";
  const isMobileSidebarOpen = hasControlledMobileSidebar
    ? parentMobileSidebarOpen
    : internalMobileOpen;
  const setIsMobileSidebarOpen = hasControlledMobileSidebar
    ? onParentMobileSidebarOpenChange
    : setInternalMobileOpen;
  const [internalLocationFilters, setInternalLocationFilters] = useState({});
  const appliedLocationFilters = catalogToolbarInParent
    ? parentAppliedLocationFilters || {}
    : internalLocationFilters;
  const setAppliedLocationFilters = catalogToolbarInParent
    ? onParentAppliedLocationFiltersChange
    : setInternalLocationFilters;
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [catalogTab, setCatalogTab] = useState("new");
  const [internalListSearch, setInternalListSearch] = useState("");
  const listSearch = catalogToolbarInParent ? parentListSearch : internalListSearch;
  const setListSearch = catalogToolbarInParent ? onParentListSearchChange : setInternalListSearch;

  // Extract vehicle filter params as stable primitives:
  // prefer URL query params, fall back to selectedVehicle from context
  const _urlSearch = location.search;

  const spareTypeVal = React.useMemo(() => {
    const p = new URLSearchParams(_urlSearch);
    return p.get('spareType') || null; // 'new' | 'used' | null
  }, [_urlSearch]);

  const effectiveSpareType = spareTypeVal || defaultFilters.spareType || null;

  const originVal = React.useMemo(() => {
    const p = new URLSearchParams(_urlSearch);
    return p.get('origin') || null; // 'OEM' | 'Aftermarket' | null
  }, [_urlSearch]);

  const vehicleMakeVal = React.useMemo(() => {
    const p = new URLSearchParams(_urlSearch);
    return p.get('vehicleMake') || p.get('maker') || selectedVehicle?.make || '';
  }, [_urlSearch, selectedVehicle?.make]);

  const vehicleModelRaw = React.useMemo(() => {
    const p = new URLSearchParams(_urlSearch);
    return p.get('vehicleModel') || p.get('model') || selectedVehicle?.model || '';
  }, [_urlSearch, selectedVehicle?.model]);

  const vehicleModelVal = React.useMemo(() => {
    return stripLeadMakeFromModel(vehicleModelRaw, vehicleMakeVal);
  }, [vehicleModelRaw, vehicleMakeVal]);

  const vehicleYearVal = React.useMemo(() => {
    const p = new URLSearchParams(_urlSearch);
    return p.get('vehicleYear') || p.get('year') || selectedVehicle?.year || '';
  }, [_urlSearch, selectedVehicle?.year]);

  const vehicleVariantVal = React.useMemo(() => {
    const p = new URLSearchParams(_urlSearch);
    const raw = p.get('vehicleVariant') || p.get('mod') || selectedVehicle?.variant || '';
    // Normalize boodmo format ("1.0L ASTA DCT/Petrol/BS6") to simple trim name ("ASTA DCT")
    if (!raw || raw === 'All' || !raw.includes('/')) return raw;
    const trimPart = raw.split('/')[0].trim();
    return trimPart.replace(/^\d+[\d.]*[Lcc]+\s*/i, '').trim() || trimPart;
  }, [_urlSearch, selectedVehicle?.variant]);

  const vehicleFuelTypeVal = React.useMemo(() => {
    const p = new URLSearchParams(_urlSearch);
    return p.get('vehicleFuelType') || selectedVehicle?.fuelType || '';
  }, [_urlSearch, selectedVehicle?.fuelType]);

  const catalogModelIdVal = React.useMemo(() => {
    const p = new URLSearchParams(_urlSearch);
    return (
      p.get('catalogModelId') ||
      selectedVehicle?.modelId ||
      selectedVehicle?.catalogModelId ||
      ''
    );
  }, [_urlSearch, selectedVehicle?.modelId, selectedVehicle?.catalogModelId]);

  const [resolvedCatalogModelId, setResolvedCatalogModelId] = useState("");

  // Resolve model id from make/model when saved vehicle is missing modelId.
  useEffect(() => {
    let cancelled = false;
    const resolveModelId = async () => {
      if (catalogModelIdVal) {
        setResolvedCatalogModelId(catalogModelIdVal);
        return;
      }
      if (!vehicleMakeVal || (!vehicleModelRaw && !vehicleModelVal)) {
        setResolvedCatalogModelId("");
        return;
      }
      try {
        const makers = await vehicleCatalogService.listMakersPublic({ includeInactive: true });
        const maker = (Array.isArray(makers) ? makers : []).find(
          (m) => String(m?.name || "").trim().toLowerCase() === String(vehicleMakeVal).trim().toLowerCase()
        );
        const makerId = maker?._id || maker?.id;
        if (!makerId) {
          if (!cancelled) setResolvedCatalogModelId("");
          return;
        }
        const models = await vehicleCatalogService.listModelsPublic(makerId, { includeInactive: true });
        const list = Array.isArray(models) ? models : [];
        const rawL = String(vehicleModelRaw || "").trim().toLowerCase();
        const normL = String(vehicleModelVal || "").trim().toLowerCase();
        const makeL = String(vehicleMakeVal || "").trim().toLowerCase();
        const compositeL = makeL && normL ? `${makeL} ${normL}` : "";
        const model =
          list.find((m) => {
            const mn = String(m?.name || "").trim().toLowerCase();
            return mn === rawL || mn === normL || (compositeL && mn === compositeL);
          }) || null;
        if (!cancelled) {
          setResolvedCatalogModelId(String(model?._id || model?.id || ""));
        }
      } catch {
        if (!cancelled) setResolvedCatalogModelId("");
      }
    };
    resolveModelId();
    return () => {
      cancelled = true;
    };
  }, [catalogModelIdVal, vehicleMakeVal, vehicleModelRaw, vehicleModelVal]);

  // Stable object built from primitives — only changes when values actually change
  const vehicleParams = React.useMemo(() => {
    if (!vehicleMakeVal && !vehicleModelVal && !vehicleYearVal && !vehicleVariantVal) return null;
    return {
      vehicleMake: vehicleMakeVal,
      vehicleModel: vehicleModelVal,
      vehicleYear: vehicleYearVal,
      vehicleVariant: vehicleVariantVal,
      vehicleFuelType: vehicleFuelTypeVal
    };
  }, [vehicleMakeVal, vehicleModelVal, vehicleYearVal, vehicleVariantVal, vehicleFuelTypeVal]);

  // Get category data from location state or use defaults
  const categoryData = React.useMemo(() => location.state?.category || {
    name: categoryName,
    slug: categorySlug || category,
    ...defaultFilters
  }, [location.state?.category, categoryName, categorySlug, category, defaultFilters]);

  const [filteredProducts, setFilteredProducts] = useState(products);
  const [loading, setLoading] = useState(false);
  const [apiProducts, setApiProducts] = useState([]);

  const normalizeCategoryToken = React.useCallback((value) => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/^\d+-/, '')
      .replace(/[_-]+/g, ' ')
      .replace(/&/g, 'and')
      .replace(/\s+/g, ' ');
  }, []);

  // Map backend product to frontend format
  const mapProductToFrontend = React.useCallback((product) => {
    // Unwrap mongoose-style payloads if API ever returns `_doc`
    const src =
      product && product._doc && typeof product._doc === "object" && !product.name
        ? {
            ...product._doc,
            matchScore: product.matchScore ?? product._doc.matchScore,
            matchTier: product.matchTier ?? product._doc.matchTier,
          }
        : product || {};
    const linkedProduct =
      src?.productId && typeof src.productId === "object" ? src.productId : null;
    const toArray = (value) => {
      if (Array.isArray(value)) return value;
      if (typeof value === "string") {
        const s = value.trim();
        if (!s) return [];
        if (s.startsWith("[") && s.endsWith("]")) {
          try {
            const parsed = JSON.parse(s);
            return Array.isArray(parsed) ? parsed : [parsed];
          } catch {
            return [s];
          }
        }
        if (s.includes(",")) {
          return s
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean);
        }
        return [s];
      }
      return value ? [value] : [];
    };
    const normalizeUrl = (v) => {
      const raw =
        typeof v === "string"
          ? v
          : (v && typeof v === "object"
              ? (v.url || v.path || v.src || v.secure_url || v.location || "")
              : "");
      const s = String(raw || "").trim().replace(/\\/g, "/");
      if (!s) return "";
      if (s === "null" || s === "undefined" || s === "[object Object]") return "";
      if (s.startsWith("data:") || s.startsWith("blob:")) return s;
      return getDocumentUrl(s);
    };
    const imageCandidatesFrom = (obj) => {
      if (!obj || typeof obj !== "object") return [];
      return [
        ...toArray(obj.images),
        ...toArray(obj.imageUrls),
        ...toArray(obj.media),
        ...toArray(obj.gallery),
        ...toArray(obj.photos),
        ...toArray(obj.attachments),
        obj.imageUrl || "",
        obj.image || "",
        obj.thumbnail || "",
        obj.thumbnailUrl || "",
        obj.partImage || "",
        obj.photo || "",
        obj.icon || "",
      ];
    };
    const allCandidateImages = [
      ...imageCandidatesFrom(src),
      ...imageCandidatesFrom(linkedProduct),
      ...imageCandidatesFrom(src?.product),
      ...imageCandidatesFrom(src?.part),
      ...imageCandidatesFrom(src?.details),
      ...imageCandidatesFrom(src?.productDetails),
    ]
      .map(normalizeUrl)
      .filter(Boolean);

    return {
      id: src._id || src.id || linkedProduct?._id || linkedProduct?.id,
      name:
        src.name ||
        linkedProduct?.name ||
        src.partName ||
        src.title ||
        "Spare Part",
      brand:
        src.brand ||
        linkedProduct?.brand ||
        src.manufacturer ||
        linkedProduct?.manufacturer ||
        "Unknown",
      partNumber:
        src.partNumber ||
        linkedProduct?.partNumber ||
        src.sku ||
        linkedProduct?.sku ||
        "N/A",
      price: Number(
        src.price ??
          linkedProduct?.price ??
          src.sellingPrice ??
          linkedProduct?.sellingPrice ??
          0
      ),
      mrp: src.mrp || linkedProduct?.mrp || src.listPrice || null,
      discount: src.discount || 0,
      image: allCandidateImages[0] || PART_PLACEHOLDER_DATA_URI,
      images: allCandidateImages,
      isOEM: (src.origin || linkedProduct?.origin) === "OEM",
      origin: src.origin || linkedProduct?.origin || "Aftermarket",
      condition: src.condition || linkedProduct?.condition || "new",
      class: src.class || "Universal",
      soldBy: src.soldBy || "Mumbai/MUM",
      deliveryDays: src.deliveryTime ? parseInt(src.deliveryTime) || 4 : 4,
      fulfilledBySparelo: true, // Default for approved products
      freeDelivery: false, // Can be added to product model later
      stock: src.stock ?? linkedProduct?.stock ?? 0,
      category:
        src.category?.name ||
        src.categoryName ||
        src.category ||
        linkedProduct?.category,
      description: src.description || linkedProduct?.description,
      // NOTE: `vehicleCompatibility` was also assigned here as
      // `src.vehicleCompatibility || []`. It is set again ~20 lines below with
      // an Array.isArray() guard, and in a JS object literal the later key
      // wins — so this one never took effect. Removed rather than kept,
      // because the two were not equivalent: the bare `||` would pass a
      // non-array value straight through, and the surviving guarded version is
      // the one whose behaviour the code already had.
      usedGrade: src.usedGrade || "",
      usedConditionRaw:
        src.usedCondition ||
        src.conditionGrade ||
        src.quality ||
        src.usedQuality ||
        src.usedPartDetails?.condition ||
        (src.usedGrade === "A"
          ? "excellent"
          : src.usedGrade === "B"
            ? "good"
            : src.usedGrade === "C"
              ? "fair"
              : ""),
      rating:
        typeof src.rating === "number"
          ? src.rating
          : typeof src.averageRating === "number"
            ? src.averageRating
            : 4.2,
      matchTier: src.matchTier,
      matchScore: src.matchScore,
      vehicleCompatibility: Array.isArray(src.vehicleCompatibility)
        ? src.vehicleCompatibility
        : [],
      partType: src.partType || linkedProduct?.partType,
      warrantyMonths: (() => {
        const raw =
          src.warrantyMonths ??
          src.warrantyPeriodMonths ??
          linkedProduct?.warrantyMonths;
        if (typeof raw === "number" && !Number.isNaN(raw)) return raw;
        const label = src.warranty || src.warrantyLabel;
        if (!label) return null;
        const s = String(label);
        const m = s.match(/(\d+)\s*(month|mo|m\b|yr|year|y\b)/i);
        if (m) {
          let n = parseInt(m[1], 10);
          if (/yr|year|^y\b/i.test(m[2])) n *= 12;
          return n;
        }
        return null;
      })(),
    };
  }, []);

  // Fetch products from API if no products provided
  useEffect(() => {
    const fetchProducts = async () => {
      // If products are passed as prop, use them (for backward compatibility)
      if (products && products.length > 0) {
        setApiProducts(products);
        if (applyUrlOriginToSidebar && originVal) {
          setSelectedOrigin([originVal]);
        }
        return;
      }

      if (!applyUrlOriginToSidebar) {
        setSelectedOrigin([]);
      }

      if (applyUrlOriginToSidebar && originVal) {
        setSelectedOrigin([originVal]);
      }

      const categorySlug = categoryData.slug || category;
      const hasVehicle =
        Boolean(vehicleParams?.vehicleMake) || Boolean(vehicleParams?.vehicleModel);

      const buildVehicleFilters = (extra = {}) => {
        const filters = { limit: 96, ...extra };
        if (vehicleParams?.vehicleMake) filters.vehicleMake = vehicleParams.vehicleMake;
        if (vehicleParams?.vehicleModel) filters.vehicleModel = vehicleParams.vehicleModel;
        if (vehicleParams?.vehicleYear) filters.vehicleYear = vehicleParams.vehicleYear;
        const split = splitVariantAndFuel(
          vehicleParams?.vehicleVariant,
          vehicleParams?.vehicleFuelType
        );
        if (split.variant) filters.vehicleVariant = split.variant;
        if (split.fuelType) filters.vehicleFuelType = split.fuelType;
        if (categorySlug) {
          const slugToCategory = {
            body: "Body",
            lighting: "Lighting",
            wheels: "Wheels",
            brakes: "Brakes",
            engine: "Engine",
            filters: "Filters",
          };
          const mapped = slugToCategory[String(categorySlug || "").toLowerCase()];
          if (mapped) filters.category = mapped;
        }
        return filters;
      };

      const fetchMarketplaceParts = async () => {
        // One call for all conditions (new + aftermarket + used) filtered by vehicle
        const data = await productService.getUserProducts(buildVehicleFilters());
        const list = data?.products ?? [];
        return list
          .filter((p) => !shouldHideCatalogPart(p))
          .map((p) => {
            const mapped = mapProductToFrontend(p);
            return {
              ...mapped,
              matchTier: p.matchTier || mapped.matchTier,
              matchScore: p.matchScore ?? mapped.matchScore,
              vehicleCompatibility: p.vehicleCompatibility || mapped.vehicleCompatibility || [],
              partType: p.partType,
              isDummy: p.isDummy,
              sourceType: p.sourceType,
            };
          });
      };

      const mergeById = (lists) => {
        const byId = new Map();
        lists.flat().forEach((item) => {
          if (item?.id) byId.set(String(item.id), item);
        });
        return [...byId.values()];
      };

      /** Keep make+model(+year) fits; variant/fuel are soft (C300 vs W206). */
      const keepVehicleRelated = (list) => {
        const cleaned = list.filter((p) => !shouldHideCatalogPart(p));
        if (!hasVehicle) return cleaned;
        const make = String(vehicleParams.vehicleMake || "").trim().toLowerCase();
        const modelRaw = String(vehicleParams.vehicleModel || "").trim().toLowerCase();
        const model = make && modelRaw.startsWith(`${make} `)
          ? modelRaw.slice(make.length).trim()
          : modelRaw;
        const yearNum = Number.parseInt(vehicleParams?.vehicleYear, 10);
        const split = splitVariantAndFuel(
          vehicleParams?.vehicleVariant,
          vehicleParams?.vehicleFuelType
        );
        const variant = String(split.variant || "").trim().toLowerCase();
        const fuelType = String(split.fuelType || "").trim().toLowerCase();

        const related = cleaned.filter((p) => {
          const tier = String(p.matchTier || "").toLowerCase();
          const score = Number(p.matchScore) || 0;
          // Backend already scored make+model+year (vehicle/exact)
          if (
            (tier === "exact" || tier === "vehicle" || score >= 90) &&
            Array.isArray(p.vehicleCompatibility) &&
            p.vehicleCompatibility.length
          ) {
            return p.vehicleCompatibility.some((row) => {
              const rowMake = String(row.make || "").trim().toLowerCase();
              return !make || rowMake === make;
            });
          }

          const vc = Array.isArray(p.vehicleCompatibility) ? p.vehicleCompatibility : [];
          if (!vc.length) return false;
          return vc.some((row) => {
            const rowMake = String(row.make || "").trim().toLowerCase();
            let rowModel = String(row.model || "").trim().toLowerCase();
            if (rowMake && rowModel.startsWith(`${rowMake} `)) {
              rowModel = rowModel.slice(rowMake.length).trim();
            }
            const from = Number.parseInt(row.yearFrom, 10);
            const to = Number.parseInt(row.yearTo, 10);
            if (make && rowMake !== make) return false;
            if (
              model &&
              rowModel !== model &&
              !rowModel.endsWith(` ${model}`) &&
              !model.endsWith(` ${rowModel}`)
            ) {
              return false;
            }
            if (!Number.isNaN(yearNum)) {
              const minOk = Number.isNaN(from) || from <= yearNum;
              const maxOk = Number.isNaN(to) || to >= yearNum;
              if (!minOk || !maxOk) return false;
            }
            // Soft preference only — do not hard-fail on trim/fuel mismatch
            void variant;
            void fuelType;
            return true;
          });
        });

        // Prefer exact/vehicle over weaker tiers when present
        const strong = related.filter(
          (p) =>
            p.matchTier === "exact" ||
            p.matchTier === "vehicle" ||
            Number(p.matchScore) >= 90
        );
        return strong.length ? strong : related;
      };

      const stampVehicleCompat = (p, { softVariant = false } = {}) => {
        const split = splitVariantAndFuel(
          vehicleParams?.vehicleVariant,
          vehicleParams?.vehicleFuelType
        );
        return {
          ...mapProductToFrontend(p),
          matchTier: "exact",
          matchScore: 100,
          vehicleCompatibility: [
            {
              make: vehicleParams?.vehicleMake,
              model: vehicleParams?.vehicleModel,
              yearFrom: vehicleParams?.vehicleYear,
              yearTo: vehicleParams?.vehicleYear,
              variant: softVariant ? "all" : split.variant || "all",
              fuelType: softVariant ? "all" : split.fuelType || "all",
            },
          ],
        };
      };

      try {
        setLoading(true);
        let vcMapped = [];

        if (categorySlug && resolvedCatalogModelId) {
          const routeSlug = String(categorySlug || "").trim();
          const wanted = normalizeCategoryToken(routeSlug);
          const modelCategories = await vehicleCatalogService.listCategoriesPublic(resolvedCatalogModelId);
          const categoryList = Array.isArray(modelCategories) ? modelCategories : [];
          const matchedCategory = categoryList.find((cat) => {
            const bySlug = normalizeCategoryToken(cat?.slug);
            const byName = normalizeCategoryToken(cat?.name);
            if (bySlug === wanted || byName === wanted) return true;
            if (wanted.length >= 3 && byName) {
              return (
                byName === wanted ||
                byName.startsWith(`${wanted} `) ||
                byName.includes(` ${wanted} `) ||
                byName.endsWith(` ${wanted}`)
              );
            }
            return false;
          });

          if (matchedCategory?._id || matchedCategory?.id) {
            const vcParts = await vehicleCatalogService.listPartsPublic(
              matchedCategory._id || matchedCategory.id,
              { modelId: resolvedCatalogModelId }
            );
            const vcList = Array.isArray(vcParts) ? vcParts : [];
            vcMapped = vcList
              .filter((p) => isLinkedSellableCatalogPart(p))
              .map((p) => stampVehicleCompat(p));
          }
        } else if (resolvedCatalogModelId && (useEnhancedCatalog || !applyUrlOriginToSidebar)) {
          // No category: only merge linked sellable catalog parts (skip name-only stubs)
          try {
            const modelCategories = await vehicleCatalogService.listCategoriesPublic(resolvedCatalogModelId);
            const categoryList = (Array.isArray(modelCategories) ? modelCategories : []).slice(0, 8);
            const batches = await Promise.all(
              categoryList.map(async (cat) => {
                const id = cat?._id || cat?.id;
                if (!id) return [];
                const vcParts = await vehicleCatalogService.listPartsPublic(id, {
                  modelId: resolvedCatalogModelId,
                });
                return (Array.isArray(vcParts) ? vcParts : [])
                  .filter((p) => isLinkedSellableCatalogPart(p))
                  .map((p) => stampVehicleCompat(p, { softVariant: true }));
              })
            );
            vcMapped = batches.flat();
          } catch {
            /* optional */
          }
        }

        let marketplace = [];
        try {
          marketplace = await fetchMarketplaceParts();
        } catch (err) {
          console.error("Failed to fetch marketplace parts:", err);
        }

        // Marketplace first so real listings win over catalog stubs with same id
        const merged = keepVehicleRelated(mergeById([marketplace, vcMapped]));

        // Only use vehicle-matched dummy preview when NO vehicle is selected
        if (merged.length === 0 && !hasVehicle && (useEnhancedCatalog || !applyUrlOriginToSidebar)) {
          setApiProducts(
            buildCatalogCompareDummyProducts({
              make: vehicleParams?.vehicleMake,
              model: vehicleParams?.vehicleModel,
              year: vehicleParams?.vehicleYear,
            })
          );
        } else {
          setApiProducts(merged);
        }
      } catch (error) {
        console.error("Failed to fetch catalog parts:", error);
        if (!hasVehicle && (useEnhancedCatalog || !applyUrlOriginToSidebar)) {
          setApiProducts(
            buildCatalogCompareDummyProducts({
              make: vehicleParams?.vehicleMake,
              model: vehicleParams?.vehicleModel,
              year: vehicleParams?.vehicleYear,
            })
          );
        } else {
          setApiProducts([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category, categoryData.slug, categoryData.name, products?.length, mapProductToFrontend, normalizeCategoryToken,
      resolvedCatalogModelId, applyUrlOriginToSidebar, effectiveSpareType, vehicleParams, originVal, useEnhancedCatalog]);

  const vehicleDummyProducts = React.useMemo(
    () =>
      buildCatalogCompareDummyProducts({
        make: vehicleMakeVal,
        model: vehicleModelVal || vehicleModelRaw,
        year: vehicleYearVal,
      }),
    [vehicleMakeVal, vehicleModelVal, vehicleModelRaw, vehicleYearVal]
  );

  // Prefer real API products. Pad with dummy ONLY when no vehicle is selected.
  const allProducts = React.useMemo(() => {
    const base = apiProducts.length > 0 ? apiProducts : products;
    const live = (base || []).filter((p) => !shouldHideCatalogPart(p));
    const hasVehicle = Boolean(vehicleMakeVal || vehicleModelVal);

    if (hasVehicle) return live;

    const needsComparePad = useEnhancedCatalog && isDarkCatalog;
    if (!needsComparePad) return live.length > 0 ? live : base;

    const source = live.length > 0 ? live : base;
    const hasOem = source.some((p) => p.condition !== "used" && (p.isOEM || p.origin === "OEM"));
    const hasAm = source.some(
      (p) => p.condition !== "used" && !(p.isOEM || p.origin === "OEM")
    );
    const hasUsed = source.some((p) => p.condition === "used");

    if (hasOem && hasAm && hasUsed && live.length > 0) return live;
    if (source.length === 0 || live.length === 0) return vehicleDummyProducts;

    const pad = [];
    if (!hasOem) {
      pad.push(...vehicleDummyProducts.filter((p) => p.isOEM || p.origin === "OEM"));
    }
    if (!hasAm) {
      pad.push(
        ...vehicleDummyProducts.filter(
          (p) => p.condition !== "used" && !(p.isOEM || p.origin === "OEM")
        )
      );
    }
    if (!hasUsed) {
      pad.push(...vehicleDummyProducts.filter((p) => p.condition === "used"));
    }
    return [...live, ...pad];
  }, [
    apiProducts,
    products,
    useEnhancedCatalog,
    isDarkCatalog,
    vehicleDummyProducts,
    vehicleMakeVal,
    vehicleModelVal,
  ]);

  // Keep initial category listing unfiltered; only explicit search should narrow results.
  useEffect(() => {
    setFilteredProducts([...allProducts]);
  }, [allProducts]);

  const listSearchNorm = listSearch.trim().toLowerCase();
  const darkPriceMin = darkPriceMinStr ? Number(darkPriceMinStr) : 0;

  const columnSourceProducts = React.useMemo(() => {
    let list = filteredProducts;

    // Dark catalog Screen-2: keep OEM + Aftermarket + Used together.
    // Do NOT strip by URL spareType/origin — columns handle that split visually.
    const keepAllTiersForCompare = useEnhancedCatalog && isDarkCatalog;

    if (!keepAllTiersForCompare) {
      if (effectiveSpareType === "used") {
        list = list.filter((p) => p.condition === "used");
      } else if (effectiveSpareType === "new") {
        list = list.filter((p) => p.condition !== "used");
      }

      if (originVal === "OEM") {
        list = list.filter((p) => p.isOEM || p.origin === "OEM");
      } else if (originVal === "Aftermarket") {
        list = list.filter((p) => p.origin === "Aftermarket" && !p.isOEM);
      }
    }

    // Sidebar Origin checkboxes (when any selected)
    if (selectedOrigin.length > 0) {
      list = list.filter((p) => {
        const isOem = p.isOEM || p.origin === "OEM";
        const isAfter = p.origin === "Aftermarket" && !p.isOEM;
        if (selectedOrigin.includes("OEM") && isOem) return true;
        if (selectedOrigin.includes("Aftermarket") && isAfter) return true;
        return false;
      });
    }

    // Brand
    if (selectedBrands.length > 0) {
      list = list.filter((p) => selectedBrands.includes(p.brand));
    }

    // Search
    if (listSearchNorm) {
      list = list.filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(listSearchNorm)) ||
          (p.brand && p.brand.toLowerCase().includes(listSearchNorm)) ||
          (p.partNumber && String(p.partNumber).toLowerCase().includes(listSearchNorm))
      );
    }

    // In stock
    if (darkInStockOnly) {
      list = list.filter((p) => (p.stock ?? 0) > 0);
    }

    // Next-day / free delivery
    if (freeDelivery) {
      list = list.filter((p) => p.freeDelivery || (p.deliveryDays ?? 99) <= 1);
    }

    // Fulfilled by Sparepe
    if (fulfilledBySparelo) {
      list = list.filter((p) => p.fulfilledBySparelo);
    }

    // Price range
    list = list.filter((p) => {
      const price = Number(p.price ?? p.mrp ?? 0) || 0;
      if (darkPriceMin > 0 && price < darkPriceMin) return false;
      if (price > darkPriceMax) return false;
      return true;
    });

    // Warranty (OR — match any selected threshold)
    if (darkWarranty6m || darkWarranty1y || darkWarranty2y) {
      list = list.filter((p) => {
        const months = p.warrantyMonths;
        if (months == null) return true;
        if (darkWarranty2y && months >= 24) return true;
        if (darkWarranty1y && months >= 12) return true;
        if (darkWarranty6m && months >= 6) return true;
        return false;
      });
    }

    // Rating (OR)
    if (darkRating4Plus || darkRating3Plus) {
      list = list.filter((p) => {
        const r = Number(p.rating ?? 0);
        if (darkRating4Plus && r >= 4) return true;
        if (darkRating3Plus && r >= 3) return true;
        return false;
      });
    }

    // Condition (OR)
    if (darkCondNew || darkCondUsedExcellent || darkCondUsedGood || darkCondUsedFair) {
      list = list.filter((p) => {
        const isUsed = p.condition === "used";
        if (darkCondNew && !isUsed) return true;
        if (!isUsed) return false;
        const grade = getProductUsedGrade(p);
        if (darkCondUsedExcellent && grade === "excellent") return true;
        if (darkCondUsedGood && (grade === "good" || grade === "unknown")) return true;
        if (darkCondUsedFair && grade === "fair") return true;
        return false;
      });
    }

    // Sort
    const sorted = [...list];
    if (sortBy === "name") {
      sorted.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    } else if (sortBy === "price-low") {
      sorted.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
    } else if (sortBy === "price-high") {
      sorted.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
    }

    return sorted;
  }, [
    filteredProducts,
    listSearchNorm,
    useEnhancedCatalog,
    isDarkCatalog,
    effectiveSpareType,
    originVal,
    selectedOrigin,
    selectedBrands,
    darkInStockOnly,
    freeDelivery,
    fulfilledBySparelo,
    darkPriceMin,
    darkPriceMax,
    darkWarranty6m,
    darkWarranty1y,
    darkWarranty2y,
    darkRating4Plus,
    darkRating3Plus,
    darkCondNew,
    darkCondUsedExcellent,
    darkCondUsedGood,
    darkCondUsedFair,
    sortBy,
  ]);

  const usedListingCount = React.useMemo(
    () => filteredProducts.filter((p) => p.condition === "used").length,
    [filteredProducts]
  );

  // Group products by classification for the three-column layout
  const groupedProducts = React.useMemo(() => {
    const groups = {
      new: [],
      aftermarket: [],
      used: []
    };

    columnSourceProducts.forEach((product) => {
      if (product.condition === "used") {
        groups.used.push(product);
      } else if (product.isOEM || product.origin === "OEM") {
        groups.new.push(product);
      } else {
        groups.aftermarket.push(product);
      }
    });

    return groups;
  }, [columnSourceProducts]);

  const darkListingColumns = React.useMemo(
    () => [
      {
        key: "new",
        title: "OEM Parts",
        badge: "Original",
        icon: "🏭",
        notice: "🔒 Factory-identical parts sourced direct from OEM.",
        hdrClass: "bg-blue-500/[0.08] border-b border-blue-500/25",
        badgeClass: "bg-blue-500/15 text-sky-400",
        noticeClass: "bg-blue-500/10 text-sky-300/90",
        iconClass: "bg-blue-500/15",
        products: groupedProducts.new,
      },
      {
        key: "aftermarket",
        title: "Aftermarket",
        badge: "3rd Party",
        icon: "🔧",
        notice: "💡 OEM-quality or better. Best price-performance ratio.",
        hdrClass: "bg-amber-500/[0.07] border-b border-amber-500/25",
        badgeClass: "bg-amber-500/15 text-amber-300",
        noticeClass: "bg-amber-500/10 text-amber-200/90",
        iconClass: "bg-amber-500/15",
        products: groupedProducts.aftermarket,
      },
      {
        key: "used",
        title: "Used Parts",
        badge: "Pre-Owned",
        icon: "♻️",
        notice: "♻️ Inspected & graded by certified mechanics.",
        hdrClass: "bg-violet-500/[0.07] border-b border-violet-500/25",
        badgeClass: "bg-violet-500/15 text-violet-300",
        noticeClass: "bg-violet-500/10 text-violet-200/90",
        iconClass: "bg-violet-500/15",
        products: groupedProducts.used,
      },
    ],
    [groupedProducts]
  );

  /** Screen-2: always show OEM | Aftermarket | Used columns (like the mockup). */
  const visibleCompareColumns = darkListingColumns;

  const handleOriginChange = (origin) => {
    setSelectedOrigin((prev) =>
      prev.includes(origin)
        ? prev.filter((o) => o !== origin)
        : [...prev, origin]
    );
  };

  const handleBrandChange = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand)
        ? prev.filter((b) => b !== brand)
        : [...prev, brand]
    );
  };

  const handleSort = (value) => {
    setSortBy(value);
  };

  // location filters come from the reusable LocationFilter component (see ../LocationFilter)

  // Get unique brands
  const uniqueBrands = [...new Set(allProducts.map((p) => p.brand))];

  const brandRows = React.useMemo(
    () =>
      uniqueBrands
        .filter(Boolean)
        .map((name) => ({
          name,
          n: allProducts.filter((p) => p.brand === name).length
        })),
    [uniqueBrands, allProducts]
  );

  const sidebarDisplayCounts = React.useMemo(() => {
    const inStock = allProducts.filter((p) => (p.stock ?? 0) > 0).length;
    const nextDay = allProducts.filter((p) => p.freeDelivery || (p.deliveryDays ?? 99) <= 1).length;
    const w6 = allProducts.filter((p) => p.warrantyMonths == null || p.warrantyMonths >= 6).length;
    const w12 = allProducts.filter((p) => p.warrantyMonths == null || p.warrantyMonths >= 12).length;
    const w24 = allProducts.filter((p) => p.warrantyMonths == null || p.warrantyMonths >= 24).length;
    const rating4 = allProducts.filter((p) => (p.rating ?? 0) >= 4).length;
    const rating3 = allProducts.filter((p) => (p.rating ?? 0) >= 3).length;
    const usedList = allProducts.filter((p) => p.condition === "used");
    const condNew = allProducts.filter((p) => p.condition !== "used").length;
    const condUsedEx = usedList.filter((p) => getProductUsedGrade(p) === "excellent").length;
    const condUsedGood = usedList.filter((p) => {
      const g = getProductUsedGrade(p);
      return g === "good" || g === "unknown";
    }).length;
    const condUsedFair = usedList.filter((p) => getProductUsedGrade(p) === "fair").length;
    return { inStock, nextDay, w6, w12, w24, rating4, rating3, condNew, condUsedEx, condUsedGood, condUsedFair };
  }, [allProducts]);

  const catalogVehicleLine = [vehicleMakeVal, vehicleModelVal, vehicleYearVal].filter(Boolean).join(" ");

  const resetCatalogFilters = () => {
    setSelectedOrigin([]);
    setSelectedBrands([]);
    setFulfilledBySparelo(false);
    setFreeDelivery(false);
    setDarkInStockOnly(false);
    setDarkRating4Plus(false);
    setDarkRating3Plus(false);
    setDarkCondNew(false);
    setDarkCondUsedExcellent(false);
    setDarkCondUsedGood(false);
    setDarkCondUsedFair(false);
    setDarkPriceMax(95000);
    setDarkPriceMinStr("");
    setDarkWarranty6m(false);
    setDarkWarranty1y(false);
    setDarkWarranty2y(false);
    setBrandsExpanded(false);
  };

  const darkFilterSidebarProps = {
    isDark: isDarkCatalog,
    vehicleLine: catalogVehicleLine || "Your vehicle",
    hasVehicle: Boolean(vehicleParams?.vehicleMake),
    uniqueBrands: brandRows,
    selectedBrands,
    onBrandToggle: handleBrandChange,
    brandsExpanded,
    onBrandsExpandedChange: setBrandsExpanded,
    priceMin: darkPriceMinStr,
    onPriceMinChange: setDarkPriceMinStr,
    priceMax: darkPriceMax,
    onPriceMaxChange: setDarkPriceMax,
    inStockOnly: darkInStockOnly,
    onInStockOnlyChange: setDarkInStockOnly,
    nextDayDelivery: freeDelivery,
    onNextDayDeliveryChange: setFreeDelivery,
    warranty6m: darkWarranty6m,
    warranty1y: darkWarranty1y,
    warranty2y: darkWarranty2y,
    onWarranty6m: setDarkWarranty6m,
    onWarranty1y: setDarkWarranty1y,
    onWarranty2y: setDarkWarranty2y,
    rating4Plus: darkRating4Plus,
    onRating4PlusChange: setDarkRating4Plus,
    rating3Plus: darkRating3Plus,
    onRating3PlusChange: setDarkRating3Plus,
    condNew: darkCondNew,
    onCondNewChange: setDarkCondNew,
    condUsedExcellent: darkCondUsedExcellent,
    onCondUsedExcellentChange: setDarkCondUsedExcellent,
    condUsedGood: darkCondUsedGood,
    onCondUsedGoodChange: setDarkCondUsedGood,
    condUsedFair: darkCondUsedFair,
    onCondUsedFairChange: setDarkCondUsedFair,
    counts: sidebarDisplayCounts,
    onReset: resetCatalogFilters,
  };
  const activeMobileFilterCount =
    selectedBrands.length +
    (darkInStockOnly ? 1 : 0) +
    (freeDelivery ? 1 : 0) +
    (darkRating4Plus ? 1 : 0) +
    (darkRating3Plus ? 1 : 0) +
    (darkWarranty6m ? 1 : 0) +
    (darkWarranty1y ? 1 : 0) +
    (darkWarranty2y ? 1 : 0) +
    (darkCondNew ? 1 : 0) +
    (darkCondUsedExcellent ? 1 : 0) +
    (darkCondUsedGood ? 1 : 0) +
    (darkCondUsedFair ? 1 : 0) +
    (darkPriceMinStr || darkPriceMax < 95000 ? 1 : 0);

  // Determine product detail route based on category
  const getProductDetailRoute = (product) =>
    getPartDetailPath(product, { categorySlug: categoryData.slug });

  return (
    <div
      className={`w-full ${
        isDarkCatalog
          ? "min-h-0 bg-transparent text-slate-200 py-0"
          : `min-h-screen bg-white ${isEmbedded ? "py-0" : "py-4 sm:py-6 md:py-8"}`
      }`}
    >
      <div className={`w-full ${useEnhancedCatalog ? "px-0 sm:px-1" : "px-3 sm:px-4 md:px-6"}`}>
        {/* Breadcrumbs */}
        {!isEmbedded && <Breadcrumbs />}

        {/* Sparepé-style hero: search + chips (only when toolbar not rendered in CataloguePage) */}
        {useEnhancedCatalog && isDarkCatalog && (
          <div className="mb-5 space-y-4">
            {!catalogToolbarInParent && (
              <>
                <div className="flex flex-col lg:flex-row gap-3 lg:items-stretch">
                  <div className="relative flex-1 flex gap-2 min-w-0">
                    <div className="relative flex-1 min-w-0">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-4 w-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        type="search"
                        value={listSearch}
                        onChange={(e) => setListSearch(e.target.value)}
                        placeholder={`Search ${(categoryData.name || categoryName || "parts").toLowerCase()}…`}
                        className="block w-full pl-10 pr-3 py-3 rounded-xl border border-slate-600/90 bg-slate-900/90 text-sm text-white placeholder:text-slate-500 focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500/60"
                      />
                    </div>
                    <button
                      type="button"
                      className="shrink-0 px-5 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-bold text-sm shadow-lg shadow-amber-900/30 transition-colors"
                    >
                      Search
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {vehicleParams?.vehicleMake && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/70 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200">
                      {[vehicleParams.vehicleMake, vehicleParams.vehicleModel].filter(Boolean).join(" ")}
                      {vehicleParams.vehicleYear ? ` ${vehicleParams.vehicleYear}` : ""}
                      <span className="text-amber-400/80">×</span>
                    </span>
                  )}
                  {(categoryData.name || categorySlug) && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/70 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-200 capitalize">
                      {(categoryData.name || categoryName || "").replace(/_/g, " ")}
                      <span className="text-amber-400/80">×</span>
                    </span>
                  )}
                  {["Brand", "Price range", "Warranty", "Availability", "Rating 4+", "Next-day delivery", "OEM fitment"].map(
                    (label) => (
                      <button
                        key={label}
                        type="button"
                        className="rounded-full border border-slate-600/80 bg-slate-800/60 px-3 py-1.5 text-xs font-medium text-slate-400 hover:border-slate-500 hover:text-slate-300 transition-colors"
                      >
                        {label}
                      </button>
                    )
                  )}
                </div>
              </>
            )}

            <div className="border-b border-slate-700/90" />
          </div>
        )}

        {/* Active Filters Banner — vehicle + spare type (hidden in dark embedded catalogue — chips above) */}
        {!isDarkCatalog && ((vehicleParams && vehicleParams.vehicleMake) || spareTypeVal) && (
          <div className="mb-4 flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Vehicle chip */}
            {vehicleParams && vehicleParams.vehicleMake && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-300 rounded-xl px-4 py-2.5 shadow-sm flex-1 min-w-0">
                <span className="text-base">&#10003;</span>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-green-700 font-bold text-xs sm:text-sm whitespace-nowrap">Vehicle:</span>
                  <span className="text-green-900 font-semibold text-xs sm:text-sm truncate">
                    {vehicleParams.vehicleMake}
                    {vehicleParams.vehicleModel ? ` ${vehicleParams.vehicleModel}` : ''}
                    {vehicleParams.vehicleYear ? ` (${vehicleParams.vehicleYear})` : ''}
                    {vehicleParams.vehicleVariant ? ` · ${vehicleParams.vehicleVariant}` : ''}
                  </span>
                  <span className="text-green-500 text-xs hidden sm:inline">
                    — compatible &amp; universal parts only
                  </span>
                </div>
                <a
                  href="/"
                  onClick={() => {
                    try { sessionStorage.setItem("jumpToVehicleSection", "1"); } catch {}
                  }}
                  className="text-xs text-green-700 hover:underline font-medium whitespace-nowrap flex-shrink-0"
                >
                  Change
                </a>
              </div>
            )}
            {/* Spare type chip */}
            {spareTypeVal && (
              <div className={`flex items-center gap-2 rounded-xl px-4 py-2.5 shadow-sm border whitespace-nowrap ${
                spareTypeVal === 'used'
                  ? 'bg-amber-50 border-amber-300 text-amber-800'
                  : 'bg-blue-50 border-blue-300 text-blue-800'
              }`}>
                <span>{spareTypeVal === 'used' ? '♻️' : '✨'}</span>
                <span className="font-bold text-xs sm:text-sm capitalize">{spareTypeVal} Spares</span>
              </div>
            )}
          </div>
        )}

        {/* Header */}
        {!isEmbedded && (
          <div className="mb-6 sm:mb-8">
          <div className="text-center max-w-2xl mx-auto mb-6">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              {categoryData.name || categoryName} Parts
            </h1>
            {description && (
              <div className="text-gray-600 text-sm sm:text-base">
                {description}
              </div>
            )}
            </div>
          </div>
        )}

        {!catalogToolbarInParent && (
          <div
            className={`flex flex-col md:flex-row items-center justify-between gap-4 pb-4 ${
              isDarkCatalog ? "border-b border-slate-700/80" : "border-b border-gray-100"
            }`}
          >
            <div className="flex items-center gap-4 order-2 md:order-1 w-full md:w-auto">
              <div
                className={`text-xs sm:text-sm font-medium ${
                  isDarkCatalog ? "text-slate-400" : "text-gray-500"
                }`}
              >
                Total {columnSourceProducts.length} part
                {columnSourceProducts.length !== 1 ? "s" : ""}
              </div>
            </div>

            {!isDarkCatalog && (
              <div className="flex items-center gap-3 w-full md:w-auto order-1 md:order-2">
                <div className="relative flex-1 md:w-64">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={listSearch}
                    onChange={(e) => setListSearch(e.target.value)}
                    placeholder={`Search ${(categoryData.name || categoryName || "parts").toLowerCase()}…`}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#131c36] focus:border-transparent text-sm bg-gray-50/50 transition-all"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main Content — filter rail sticks while product columns scroll */}
        <div
          className={
            useEnhancedCatalog
              ? "flex flex-col items-stretch gap-4 pt-1 md:flex-row md:items-start md:gap-4 lg:gap-5 sm:pt-2"
              : "flex flex-row items-start gap-2 sm:gap-4 lg:gap-6"
          }
        >
          {/* Left Sidebar — sticky after scroll so left column never looks empty */}
          <div
            className={`flex-shrink-0 space-y-4 ${
              useEnhancedCatalog
                ? "order-1 hidden w-56 self-start md:sticky md:top-2 md:z-30 md:block md:max-h-[calc(100vh-1rem)] lg:top-2 lg:w-64 lg:max-h-[calc(100vh-1rem)]"
                : "order-1 w-[6.5rem] min-[400px]:w-32 sm:w-48 lg:w-64"
            }`}
          >
            {useEnhancedCatalog ? (
              <CatalogDarkFilterSidebar {...darkFilterSidebarProps} />
            ) : (
              <>
                <div className="hidden lg:block">
                  <CatalogueSidebar
                    isMobileOpen={isMobileSidebarOpen}
                    setIsMobileOpen={setIsMobileSidebarOpen}
                  />
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-2 shadow-sm sm:p-4 lg:p-5">
                  <div className="mb-3 flex items-center justify-between border-b border-gray-200 pb-2 sm:mb-4 sm:pb-3">
                    <h3 className="text-[10px] font-semibold text-gray-800 sm:text-sm">Filters</h3>
                    <button
                      type="button"
                      onClick={resetCatalogFilters}
                      className="text-[8px] font-semibold text-amber-600 hover:text-amber-700 sm:text-xs"
                    >
                      RESET
                    </button>
                  </div>

                  <div className="mb-3 sm:mb-4">
                    <h4 className="font-semibold text-[9px] sm:text-xs text-gray-800 mb-2">Origin</h4>
                    <div className="space-y-1.5 sm:space-y-2">
                      <label className="flex min-w-0 items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedOrigin.includes("Aftermarket")}
                          onChange={() => handleOriginChange("Aftermarket")}
                          className="h-3 w-3 shrink-0 rounded border-gray-300 text-[#131c36] focus:ring-[#131c36] sm:h-4 sm:w-4"
                        />
                        <span className="ml-1.5 truncate text-[8px] text-gray-700 sm:ml-2 sm:text-xs">
                          Aftermarket <span className="text-sky-600">({allProducts.filter((p) => !p.isOEM).length})</span>
                        </span>
                      </label>
                      <label className="flex min-w-0 items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedOrigin.includes("OEM")}
                          onChange={() => handleOriginChange("OEM")}
                          className="h-3 w-3 shrink-0 rounded border-gray-300 text-[#131c36] focus:ring-[#131c36] sm:h-4 sm:w-4"
                        />
                        <span className="ml-1.5 truncate text-[8px] text-gray-700 sm:ml-2 sm:text-xs">
                          OEM <span className="text-sky-600">({allProducts.filter((p) => p.isOEM).length})</span>
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mb-4 hidden sm:block">
                    <h4 className="font-semibold text-[9px] sm:text-xs text-gray-800 mb-2">Fulfilled by Sparepe</h4>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={fulfilledBySparelo}
                        onChange={(e) => setFulfilledBySparelo(e.target.checked)}
                        className="w-4 h-4 text-[#131c36] border-gray-300 rounded focus:ring-[#131c36]"
                      />
                      <span className="ml-2 text-[9px] sm:text-xs text-gray-700">
                        Fulfilled by Sparepe ({allProducts.filter((p) => p.fulfilledBySparelo).length})
                      </span>
                    </label>
                  </div>

                  <div className="mb-4 hidden sm:block">
                    <h4 className="font-semibold text-[9px] sm:text-xs text-gray-800 mb-2">Free Delivery</h4>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={freeDelivery}
                        onChange={(e) => setFreeDelivery(e.target.checked)}
                        className="w-4 h-4 text-[#131c36] border-gray-300 rounded focus:ring-[#131c36]"
                      />
                      <span className="ml-2 text-[9px] sm:text-xs text-gray-700">
                        Free Delivery ({allProducts.filter((p) => p.freeDelivery).length})
                      </span>
                    </label>
                  </div>

                  <div className="mb-3 sm:mb-4">
                    <h4 className="font-semibold text-[9px] sm:text-xs text-gray-800 mb-2">Brand</h4>
                    <div className="space-y-1.5 sm:space-y-2">
                      {uniqueBrands.map((brand) => {
                        const count = allProducts.filter((p) => p.brand === brand).length;
                        return (
                          <label key={brand} className="flex min-w-0 items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedBrands.includes(brand)}
                              onChange={() => handleBrandChange(brand)}
                              className="h-3 w-3 shrink-0 rounded border-gray-300 text-[#131c36] focus:ring-[#131c36] sm:h-4 sm:w-4"
                            />
                            <span className="ml-1.5 truncate text-[8px] text-gray-700 sm:ml-2 sm:text-xs">
                              {brand} ({count})
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right Content - Products */}
          <div className="order-2 min-w-0 w-full flex-1">
            {useEnhancedCatalog && (
              <div
                className={`mb-3 flex gap-2 overflow-x-auto border-y px-1 py-2 md:hidden ${
                  isDarkCatalog
                    ? "border-slate-700 bg-[#0b1120]"
                    : "border-slate-200 bg-slate-50"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setIsMobileSidebarOpen(true)}
                  className={`relative flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-bold ${
                    activeMobileFilterCount > 0
                      ? "border-blue-600 bg-blue-600 text-white"
                      : isDarkCatalog
                        ? "border-slate-600 bg-slate-800 text-slate-100"
                        : "border-slate-300 bg-white text-slate-800"
                  }`}
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  Filters
                  {activeMobileFilterCount > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-white px-1 text-[9px] font-black text-blue-700">
                      {activeMobileFilterCount}
                    </span>
                  )}
                </button>

                <label
                  className={`relative flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-bold ${
                    isDarkCatalog
                      ? "border-slate-600 bg-slate-800 text-slate-100"
                      : "border-slate-300 bg-white text-slate-800"
                  }`}
                >
                  <ArrowUpDown className="h-3.5 w-3.5" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none border-0 bg-transparent p-0 pr-3 text-xs font-bold text-inherit outline-none"
                    aria-label="Sort products"
                  >
                    <option value="relevance">Sort</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="name">Name</option>
                  </select>
                  <span className="pointer-events-none absolute right-2 text-[9px]">▼</span>
                </label>

                <button
                  type="button"
                  onClick={() => setDarkInStockOnly((value) => !value)}
                  className={`shrink-0 rounded-lg border px-3 py-2 text-xs font-semibold ${
                    darkInStockOnly
                      ? "border-emerald-600 bg-emerald-600 text-white"
                      : isDarkCatalog
                        ? "border-slate-600 bg-slate-800 text-slate-200"
                        : "border-slate-300 bg-white text-slate-700"
                  }`}
                >
                  In Stock
                </button>

                <button
                  type="button"
                  onClick={() => setIsMobileSidebarOpen(true)}
                  className={`shrink-0 rounded-lg border px-3 py-2 text-xs font-semibold ${
                    selectedBrands.length > 0
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : isDarkCatalog
                        ? "border-slate-600 bg-slate-800 text-slate-200"
                        : "border-slate-300 bg-white text-slate-700"
                  }`}
                >
                  Brand{selectedBrands.length > 0 ? ` (${selectedBrands.length})` : ""}
                </button>

                <button
                  type="button"
                  onClick={() => setIsMobileSidebarOpen(true)}
                  className={`shrink-0 rounded-lg border px-3 py-2 text-xs font-semibold ${
                    darkPriceMinStr || darkPriceMax < 95000
                      ? "border-blue-600 bg-blue-50 text-blue-700"
                      : isDarkCatalog
                        ? "border-slate-600 bg-slate-800 text-slate-200"
                        : "border-slate-300 bg-white text-slate-700"
                  }`}
                >
                  Price
                </button>

                {activeMobileFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={resetCatalogFilters}
                    className="shrink-0 px-2 py-2 text-xs font-bold text-rose-500"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
            {(() => {
              if (loading) {
                return (
                  <div className="flex w-full min-h-[min(70vh,40rem)] items-center justify-center py-10 px-2 sm:px-4">
                    <div
                      className={`w-full max-w-4xl mx-auto text-center py-24 rounded-3xl border shadow-sm ${
                        isDarkCatalog
                          ? "bg-slate-900/90 border-slate-700"
                          : "bg-white border-gray-100"
                      }`}
                    >
                      <div className="relative w-20 h-20 mx-auto mb-6">
                        <div
                          className={`absolute inset-0 rounded-full border-4 ${
                            isDarkCatalog ? "border-slate-700" : "border-gray-100"
                          }`}
                        />
                        <div
                          className={`absolute inset-0 rounded-full border-4 border-t-transparent animate-spin ${
                            isDarkCatalog ? "border-amber-500" : "border-blue-600"
                          }`}
                        />
                      </div>
                      <p
                        className={`font-black uppercase tracking-widest text-xs ${
                          isDarkCatalog ? "text-slate-300" : "text-gray-900"
                        }`}
                      >
                        Fetching Spares...
                      </p>
                    </div>
                  </div>
                );
              }
              const displayProducts = columnSourceProducts;
              if (displayProducts.length === 0) {
                const filtersActive = allProducts.length > 0;
                return (
                  <div className="flex w-full min-h-[min(50vh,28rem)] items-center justify-center py-10 px-2 sm:px-4">
                    <div
                      className={`w-full max-w-4xl mx-auto border rounded-[2.5rem] p-8 sm:p-16 text-center shadow-2xl ${
                        isDarkCatalog
                          ? "bg-slate-900/95 border-slate-700 shadow-black/40"
                          : "bg-white border-gray-100 shadow-gray-100/50"
                      }`}
                    >
                      <h3
                        className={`text-2xl sm:text-3xl font-black mb-4 uppercase tracking-tighter ${
                          isDarkCatalog ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {filtersActive
                          ? "No parts match selected filters"
                          : `No ${spareTypeVal ? `${spareTypeVal} ` : ""}Parts Found`}
                      </h3>
                      {filtersActive ? (
                        <p
                          className={`max-w-md mx-auto mb-8 font-medium text-base leading-relaxed ${
                            isDarkCatalog ? "text-slate-400" : "text-gray-500"
                          }`}
                        >
                          Try changing brand, price, condition, or other filters — or clear them to see all results.
                        </p>
                      ) : vehicleParams && vehicleParams.vehicleMake ? (
                        <p
                          className={`max-w-md mx-auto mb-10 font-medium text-lg leading-relaxed ${
                            isDarkCatalog ? "text-slate-400" : "text-gray-500"
                          }`}
                        >
                          We couldn&apos;t find any compatible{" "}
                          <span className={isDarkCatalog ? "text-amber-400 font-bold" : "text-blue-600 font-bold"}>
                            {categoryName}
                          </span>{" "}
                          spares for your{" "}
                          <span className={isDarkCatalog ? "text-white font-bold" : "text-gray-900 font-bold"}>
                            {[vehicleParams.vehicleMake, vehicleParams.vehicleModel].filter(Boolean).join(" ")}
                          </span>
                          .
                        </p>
                      ) : (
                        <p
                          className={`max-w-md mx-auto mb-10 font-medium text-lg leading-relaxed ${
                            isDarkCatalog ? "text-slate-400" : "text-gray-500"
                          }`}
                        >
                          Please try different filters or explore other categories.
                        </p>
                      )}
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                        <button
                          type="button"
                          onClick={resetCatalogFilters}
                          className={
                            isDarkCatalog
                              ? "px-10 py-4 font-black rounded-2xl transition-all shadow-2xl uppercase text-xs tracking-widest bg-amber-500 text-slate-900 hover:bg-amber-400 shadow-amber-900/20"
                              : "sp-btn-on-dark px-10 py-4 font-black rounded-2xl transition-all shadow-2xl uppercase text-xs tracking-widest bg-gray-900 text-white hover:bg-black shadow-gray-200"
                          }
                        >
                          Clear All Filters
                        </button>
                        {!filtersActive && (
                          <a
                            href="/"
                            onClick={() => {
                              try { sessionStorage.setItem("jumpToVehicleSection", "1"); } catch {}
                            }}
                            className={`font-black text-xs uppercase tracking-widest underline decoration-2 underline-offset-8 transition-colors ${
                              isDarkCatalog
                                ? "text-amber-400 hover:text-amber-300"
                                : "text-gray-900 hover:text-blue-600"
                            }`}
                          >
                            Change Vehicle
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <div className={useEnhancedCatalog ? "min-w-0" : ""}>
                  <div
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-1 py-3 ${
                      isDarkCatalog ? "border-b border-slate-700/60 mb-5" : useEnhancedCatalog ? "border-b border-slate-200 mb-5" : ""
                    }`}
                  >
                    <p className={`text-[10px] sm:text-sm leading-snug ${isDarkCatalog ? "text-slate-400" : "text-gray-600"}`}>
                      <span className={`font-bold ${isDarkCatalog ? "text-slate-100" : "text-gray-900"}`}>
                        Showing {displayProducts.length} result{displayProducts.length !== 1 ? "s" : ""}
                      </span>
                      <span className={`${isDarkCatalog ? "text-slate-500" : "text-gray-400"} hidden sm:inline`}>
                        {effectiveSpareType === "used"
                          ? " — Used Parts highlighted"
                          : " — OEM · Aftermarket · Used"}
                      </span>
                    </p>
                  </div>
                  {(() => {
                    if (useEnhancedCatalog && isDarkCatalog) {
                      const cols = visibleCompareColumns;
                      const filledCols = cols.filter((c) => c.products.length > 0);
                      const emptyCols = cols.filter((c) => c.products.length === 0);

                      const renderColumn = (column, index = 0) => {
                        const isEmpty = column.products.length === 0;
                        const isFirstCompareColumn = index === 0;
                        return (
                          <section
                            key={column.key}
                            className={`flex min-w-0 w-full flex-col overflow-hidden rounded-xl border border-slate-700/80 bg-slate-900/60 shadow-lg shadow-black/20 ${
                              isEmpty
                                ? "lg:sticky lg:top-2 lg:z-30 lg:self-start"
                                : isFirstCompareColumn
                                  ? "lg:sticky lg:top-2 lg:z-20 lg:self-start"
                                  : ""
                            }`}
                          >
                            <div
                              className={`flex min-h-[3.25rem] shrink-0 items-center gap-2 px-3.5 py-3 ${column.hdrClass}`}
                            >
                              <div
                                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm ${column.iconClass}`}
                              >
                                {column.icon}
                              </div>
                              <div className="min-w-0 flex flex-wrap items-center gap-1.5">
                                <span className="text-sm font-bold text-slate-100">
                                  {column.title}
                                </span>
                                <span
                                  className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${column.badgeClass}`}
                                >
                                  {column.badge}
                                </span>
                              </div>
                              <span className="ml-auto shrink-0 text-[11px] font-medium text-slate-400">
                                {column.products.length} result
                                {column.products.length === 1 ? "" : "s"}
                              </span>
                            </div>

                            <p
                              className={`mx-3 mt-2.5 mb-1 flex min-h-[2.75rem] shrink-0 items-start gap-1.5 rounded-lg px-3 py-2 text-[11px] leading-snug ${column.noticeClass}`}
                            >
                              {column.notice}
                            </p>

                            <div className="flex min-h-0 flex-1 flex-col">
                              {!isEmpty ? (
                                column.products.map((product) => (
                                  <CatalogueComparePartCard
                                    key={`${column.key}-${product.id}`}
                                    product={product}
                                    categoryData={categoryData}
                                    columnKey={column.key}
                                  />
                                ))
                              ) : (
                                <div className="flex flex-1 flex-col items-center justify-start gap-2 px-4 pb-8 pt-6 text-center">
                                  <span className="text-2xl opacity-40" aria-hidden>
                                    {column.icon}
                                  </span>
                                  <p className="text-xs font-semibold text-slate-400">
                                    No {column.title.toLowerCase()} found
                                  </p>
                                  <p className="max-w-[14rem] text-[10px] leading-relaxed text-slate-600">
                                    Matching parts for this vehicle will show here when listed.
                                  </p>
                                </div>
                              )}
                            </div>

                            {column.products.length > 3 && (
                              <div className="mt-auto shrink-0 border-t border-slate-700/60 px-3 py-2.5 text-right">
                                <span className="cursor-pointer text-[11px] text-amber-400 hover:text-amber-300">
                                  View all {column.products.length} results →
                                </span>
                              </div>
                            )}
                          </section>
                        );
                      };

                      return (
                        <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 lg:items-start lg:gap-4">
                          {cols.map((column, index) => renderColumn(column, index))}
                        </div>
                      );
                    }

                    return (
                      <div
                        className={
                          useEnhancedCatalog
                            ? "grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 lg:gap-4"
                            : "grid grid-cols-2 gap-2 pt-2 sm:gap-4 md:grid-cols-3 xl:grid-cols-4"
                        }
                      >
                        {displayProducts.length > 0 ? (
                          displayProducts.map((product, index) =>
                            useEnhancedCatalog ? (
                              <CatalogueDarkPartCard
                                key={product.id}
                                product={product}
                                index={index}
                                categoryData={categoryData}
                                layout="grid"
                              />
                            ) : (
                              <ClassificationTableProductCard
                                key={product.id}
                                product={product}
                                getProductDetailRoute={getProductDetailRoute}
                                categoryData={categoryData}
                              />
                            )
                          )
                        ) : (
                          <div
                            className={`col-span-full p-16 text-center font-black uppercase tracking-widest text-xs ${
                              isDarkCatalog ? "text-slate-500" : "text-gray-500"
                            }`}
                          >
                            No parts found in this category
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })()}
          </div>
        </div>

        {useEnhancedCatalog && isMobileSidebarOpen && (
          <div className="fixed inset-0 z-[120] md:hidden" role="dialog" aria-modal="true" aria-label="Product filters">
            <button
              type="button"
              aria-label="Close filters"
              onClick={() => setIsMobileSidebarOpen(false)}
              className="absolute inset-0 bg-black/55 backdrop-blur-[1px]"
            />
            <div
              className={`absolute inset-x-0 bottom-0 max-h-[88vh] overflow-y-auto rounded-t-3xl border-t p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-2xl ${
                isDarkCatalog
                  ? "border-slate-700 bg-[#0b1120]"
                  : "border-slate-200 bg-slate-50"
              }`}
            >
              <div
                className={`sticky top-0 z-10 mb-3 flex items-center justify-between border-b pb-3 ${
                  isDarkCatalog
                    ? "border-slate-700 bg-[#0b1120]"
                    : "border-slate-200 bg-slate-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <SlidersHorizontal className="h-5 w-5" />
                  <h2 className="text-base font-bold">
                    Filters{activeMobileFilterCount > 0 ? ` (${activeMobileFilterCount})` : ""}
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  {activeMobileFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={resetCatalogFilters}
                      className="px-2 py-1 text-xs font-bold text-rose-500"
                    >
                      Reset
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsMobileSidebarOpen(false)}
                    className={`rounded-full p-2 ${
                      isDarkCatalog
                        ? "bg-slate-800 text-slate-200"
                        : "bg-white text-slate-700 shadow-sm"
                    }`}
                    aria-label="Close filters"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <CatalogDarkFilterSidebar {...darkFilterSidebarProps} />

              <button
                type="button"
                onClick={() => setIsMobileSidebarOpen(false)}
                className="sticky bottom-0 mt-4 w-full rounded-xl bg-amber-500 px-4 py-3.5 text-sm font-black text-slate-950 shadow-lg"
              >
                Show {columnSourceProducts.length} result{columnSourceProducts.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer Content */}
      {footerContent && (
        <div className="mt-8 px-3 sm:px-4 md:px-6">
          {footerContent}
        </div>
      )}

      <TierComparisonModal 
        isOpen={showCompareModal} 
        onClose={() => setShowCompareModal(false)} 
        vehicleInfo={vehicleMakeVal ? `${vehicleMakeVal} ${vehicleModelVal}` : null}
      />
    </div>
  );
};

export default CategoryProductList;

